import { useState } from "react";
import { TextField } from "@mui/material";

export type InlineTextEditorProps = {
  /// Seeds the local draft. Read once on mount — mount the editor with a `key`
  /// that changes when a different value should be edited.
  initialValue: string;
  /// Called with the draft when the edit is committed, and only if it differs
  /// from `initialValue`.
  onCommit: (value: string) => void;
  /// Called after a commit and on Escape. The caller closes the editor here.
  onClose: () => void;
  type?: "text" | "number";
  multiline?: boolean;
  placeholder?: string;
  step?: string;
  "aria-label"?: string;
};

/**
 * A text input that owns the value while it is being typed in.
 *
 * The transaction editors derive their displayed value from a server preview,
 * so a field wired straight to `onChange` snaps back to the stored value
 * between keystrokes and fires a preview request per character. Holding the
 * draft locally and committing on blur/Enter gives one round-trip and one
 * pending edit per field. Escape abandons the draft.
 *
 * A `type="number"` editor also selects its contents on focus — see `onFocus`.
 */
export const InlineTextEditor = ({
  initialValue,
  onCommit,
  onClose,
  type = "text",
  multiline = false,
  placeholder,
  step,
  "aria-label": ariaLabel,
}: InlineTextEditorProps) => {
  const [draft, setDraft] = useState(initialValue);

  const commit = () => {
    if (draft !== initialValue) onCommit(draft);
    onClose();
  };

  return (
    <TextField
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      // A number is replaced wholesale — the caret landing mid-`215.50` and
      // appending is never what was meant. Text is left alone: adding a word to
      // a description is ordinary, and select-all would fight it.
      onFocus={(e) => {
        if (type === "number") e.target.select();
      }}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          onClose();
        }
        // Shift+Enter inserts a newline in a multiline field rather than
        // committing.
        if (e.key === "Enter" && !(multiline && e.shiftKey)) {
          e.preventDefault();
          commit();
        }
      }}
      type={type}
      multiline={multiline}
      placeholder={placeholder}
      slotProps={{ htmlInput: { step, "aria-label": ariaLabel } }}
      autoFocus
      fullWidth
      size="small"
    />
  );
};
